import { Spin } from 'antd';
import { useEffect, useRef, useState } from 'react';

import { Button } from '@/components/common/button';
import { Tabs } from '@/components/common/tabs';
import { Drawer } from '@/components/feedback/drawer';
import { EmptyState } from '@/components/feedback/empty-state';
import { ErrorState } from '@/components/feedback/error-state';

import { TICKETS_PAGE_SIZE } from '../constants';
import { useOpenTicketCount, useSupportTickets } from '../hooks';
import type { TicketTab } from '../types';

import styles from './support-listing-drawer.module.scss';
import { SupportTicketRow } from './support-ticket-row';
import { SupportTicketRowSkeleton } from './support-ticket-row-skeleton';

export type SupportListingDrawerProps = {
  open: boolean;
  onClose: () => void;
  onOpenTicket: (id: string) => void;
  onRaise: () => void;
};

/**
 * Customer Support listing (Figma 2389-764730 / 2389-766708). Active/Closed tabs (Active
 * carries the open-ticket count badge), a latest-first list revealed in windows of 20 via an
 * IntersectionObserver sentinel, empty + error states, and an always-visible Raise button
 * (story 101970). No realtime — refetches on open (story 101970 §9).
 */
export function SupportListingDrawer({
  open,
  onClose,
  onOpenTicket,
  onRaise,
}: SupportListingDrawerProps) {
  const [tab, setTab] = useState<TicketTab>('active');

  // Reset to Active each time the drawer (re)opens — no cross-session tab persistence.
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) setTab('active');
  }

  const { data: activeCount } = useOpenTicketCount(open);

  return (
    <Drawer open={open} onClose={onClose} title="Customer Support" cancelText="" width={400}>
      <div className={styles.body}>
        <Tabs
          variant="pill"
          activeKey={tab}
          onChange={(k) => setTab(k as TicketTab)}
          items={[
            {
              key: 'active',
              label: (
                <span className={styles.tabLabel}>
                  Active
                  {activeCount && activeCount > 0 ? (
                    <span className={styles.badge}>{activeCount}</span>
                  ) : null}
                </span>
              ),
            },
            { key: 'closed', label: 'Closed' },
          ]}
        />

        <TicketList tab={tab} open={open} onOpenTicket={onOpenTicket} />

        <div className={styles.footer}>
          <Button variant="neutral" onClick={onRaise}>
            Raise Support Ticket
          </Button>
        </div>
      </div>
    </Drawer>
  );
}

function TicketList({
  tab,
  open,
  onOpenTicket,
}: {
  tab: TicketTab;
  open: boolean;
  onOpenTicket: (id: string) => void;
}) {
  const { data, isLoading, isError, refetch } = useSupportTickets(tab, open);
  const [visible, setVisible] = useState(TICKETS_PAGE_SIZE);

  // Reset the reveal window when the tab changes (render-phase state update, no effect needed).
  const [lastTab, setLastTab] = useState(tab);
  if (tab !== lastTab) {
    setLastTab(tab);
    setVisible(TICKETS_PAGE_SIZE);
  }

  const tickets = data ?? [];
  const shown = tickets.slice(0, visible);
  const hasMore = visible < tickets.length;

  const sentinelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!hasMore || typeof IntersectionObserver === 'undefined') return;
    const el = sentinelRef.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting) setVisible((v) => v + TICKETS_PAGE_SIZE);
    });
    io.observe(el);
    return () => io.disconnect();
  }, [hasMore]);

  if (isLoading) {
    return <SupportTicketRowSkeleton count={6} />;
  }

  if (isError) {
    return (
      <ErrorState
        title="Unable to load support tickets"
        description="Please check your internet connection and try again."
        onRetry={() => void refetch()}
      />
    );
  }

  if (tickets.length === 0) {
    return <EmptyState title="No tickets" description="You have no tickets in this tab." />;
  }

  return (
    <div className={styles.list}>
      {shown.map((t) => (
        <SupportTicketRow key={t.id} ticket={t} onOpen={onOpenTicket} />
      ))}
      {hasMore ? (
        <div ref={sentinelRef} className={styles.center} aria-busy="true">
          <Spin size="small" />
        </div>
      ) : null}
    </div>
  );
}
